import { useState, useEffect, useCallback } from 'react';
import { useParkingLotMap } from '@/hooks/useParkingLotMap';
import { ParkingLotSetupPlaceholder } from './ParkingLotSetupPlaceholder';
import { ParkingLotImageUploadDialog } from './ParkingLotImageUploadDialog';
import { ParkingLotCalibrationView } from './ParkingLotCalibrationView';
import { ParkingLotCanvasView } from './ParkingLotCanvasView';
import { Loader2 } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ParkingLotMapViewProps {
  shopId: string | undefined;
}

export function ParkingLotMapView({ shopId }: ParkingLotMapViewProps) {
  const {
    layout,
    spots,
    tickets,
    isLoading,
    parkingLotImage,
    fetchData,
    saveSpots,
    assignTicketToSpot,
  } = useParkingLotMap(shopId);

  const { isManager, isServiceWriter } = usePermissions();
  const canEdit = isManager || isServiceWriter;
  const { toast } = useToast();

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editorSpots, setEditorSpots] = useState<any[]>([]);

  // Sync spots to editor state when data loads
  useEffect(() => {
    if (spots.length > 0) {
      setEditorSpots(spots.map(s => ({
        id: s.id,
        label: s.label,
        spot_type: s.spot_type,
        geom_px: s.geom_px || null,
        is_active: s.is_active,
      })));
    }
  }, [spots]);

  const handleUploadComplete = useCallback((imageId: string) => {
    setShowUploadDialog(false);
    fetchData();
  }, [fetchData]);

  const handleCalibrationComplete = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleActivate = useCallback(async () => {
    if (!parkingLotImage) return;

    try {
      const { error } = await supabase.rpc('activate_parking_lot_image', {
        p_image_id: parkingLotImage.id,
      });

      if (error) throw error;

      toast({
        title: 'Image activated',
        description: 'You can now create parking spots on the layout.',
      });

      fetchData();
    } catch (err: any) {
      console.error('Activation error:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to activate image',
        variant: 'destructive',
      });
    }
  }, [parkingLotImage, fetchData, toast]);

  const handleSaveSpots = useCallback(async () => {
    if (!parkingLotImage) return false;

    // Convert editor spots to format expected by saveSpots
    // Stage 0D — the id travels with the spot so the save edits it in place:
    // a vehicle parked in it keeps its spot (replace_parking_lot_spots diffs
    // by id). Spots drawn in this session have a temp id and are created.
    const spotsToSave = editorSpots.map(s => ({
      id: s.id,
      label: s.label,
      spot_type: s.spot_type || 'STANDARD',
      geom_px: s.geom_px,
      is_active: s.is_active ?? true,
    }));

    return saveSpots(spotsToSave);
  }, [editorSpots, parkingLotImage, saveSpots]);

  const handleAssignTicket = useCallback(async (ticketId: string, spotId: string | null) => {
    return assignTicketToSpot(ticketId, spotId);
  }, [assignTicketToSpot]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!shopId) {
    return (
      <div className="flex items-center justify-center h-[600px] text-muted-foreground">
        No shop selected
      </div>
    );
  }

  // No image uploaded yet - show placeholder
  if (!parkingLotImage) {
    return (
      <>
        <ParkingLotSetupPlaceholder
          canEdit={canEdit}
          onUpload={() => setShowUploadDialog(true)}
        />
        <ParkingLotImageUploadDialog
          open={showUploadDialog}
          onOpenChange={setShowUploadDialog}
          shopId={shopId}
          onUploadComplete={handleUploadComplete}
        />
      </>
    );
  }

  // Image pending calibration
  if (parkingLotImage.status === 'PENDING') {
    return (
      <ParkingLotCalibrationView
        image={parkingLotImage}
        onComplete={handleCalibrationComplete}
        onActivate={handleActivate}
      />
    );
  }

  // Image calibrated but not yet activated - show calibration view with activate button
  if (parkingLotImage.status === 'CALIBRATED') {
    return (
      <ParkingLotCalibrationView
        image={parkingLotImage}
        onComplete={handleCalibrationComplete}
        onActivate={handleActivate}
      />
    );
  }

  // Active image - show canvas view
  return (
    <ParkingLotCanvasView
      shopId={shopId}
      image={parkingLotImage}
      spots={editorSpots}
      tickets={tickets}
      onSpotsChange={setEditorSpots}
      onSaveSpots={handleSaveSpots}
      onAssignTicket={handleAssignTicket}
    />
  );
}
